import React, { useEffect } from 'react';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function focusableIn(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE));
}

/**
 * Keeps the keyboard with an open sheet or flyout.
 *
 * On open it moves focus to the first control inside. Escape calls `onClose`. With `trapTab`
 * (a modal sheet) Tab and Shift+Tab wrap around inside the container; without it (a disclosure
 * such as the rail flyout) Tab may leave. On close, focus goes back to the control that opened
 * it — unless the reader already moved focus somewhere else.
 */
export function useFocusTrap(
  containerRef: React.RefObject<HTMLElement | null>,
  isOpen: boolean,
  onClose: () => void,
  { trapTab = true }: { trapTab?: boolean } = {}
): void {
  useEffect(() => {
    const container = containerRef.current;
    if (!isOpen || container === null) {
      return undefined;
    }
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    focusableIn(container)[0]?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== 'Tab' || !trapTab) {
        return;
      }
      const items = focusableIn(container);
      const first = items[0];
      const last = items[items.length - 1];
      if (first === undefined || last === undefined) {
        event.preventDefault();
        return;
      }
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      const active = document.activeElement;
      if (active === null || active === document.body || container.contains(active)) {
        previous?.focus();
      }
    };
  }, [containerRef, isOpen, onClose, trapTab]);
}
